import { reactive, computed } from 'vue'
import { clearAuthToken } from './api'

export type UserRole = 'admin-master' | 'admin-obra' | 'user'

export interface SessionUser {
  name: string
  role: UserRole
  username: string
  systemId?: string
  systemUsername?: string
  avatar?: string
  uid: string
  roleId?: string
  subordinateUsernames?: string[]
  hasSubordinates?: boolean
  suspended?: boolean
  isAnalyst?: boolean
}

const storageKey = 'rdo_session'
const persistKey = 'rdo_keep_logged_in'

interface LegacySessionUser {
  name?: string
  role?: UserRole
  username?: string
  systemId?: string
  systemUsername?: string
  avatar?: string
  uid?: string
  roleId?: string
  subordinateUsernames?: unknown
  hasSubordinates?: unknown
  isAnalyst?: unknown
}

function loadSession(): SessionUser | null {
  try {
    const raw = window.sessionStorage.getItem(storageKey) || window.localStorage.getItem(storageKey)
    if (!raw) return null
    const parsed = JSON.parse(raw) as LegacySessionUser
    if (!parsed?.username) {
      return {
        name: parsed?.name || '',
        role: parsed?.role || 'user',
        username: parsed?.name || '',
        systemId: parsed.systemId || '',
        systemUsername: parsed.systemUsername || '',
        avatar: parsed.avatar || '',
        uid: parsed.uid || '',
        roleId: parsed.roleId || '',
        subordinateUsernames: Array.isArray(parsed.subordinateUsernames)
          ? parsed.subordinateUsernames.filter((value): value is string => typeof value === 'string')
          : [],
        hasSubordinates: Boolean(parsed.hasSubordinates),
        isAnalyst: Boolean(parsed.isAnalyst)
      }
    }
    return {
      name: parsed.name || parsed.username,
      role: parsed.role || 'user',
      username: parsed.username,
      systemId: parsed.systemId || '',
      systemUsername: parsed.systemUsername || '',
      avatar: parsed.avatar || '',
      uid: parsed.uid || '',
      roleId: parsed.roleId || '',
      subordinateUsernames: Array.isArray(parsed.subordinateUsernames)
        ? parsed.subordinateUsernames.filter((value): value is string => typeof value === 'string')
        : [],
      hasSubordinates: Boolean(parsed.hasSubordinates),
      isAnalyst: Boolean(parsed.isAnalyst)
    }
  } catch {
    return null
  }
}

function saveSession(user: SessionUser | null, persistent = false) {
  try {
    if (!user) {
      window.localStorage.removeItem(storageKey)
      window.sessionStorage.removeItem(storageKey)
      window.localStorage.removeItem(persistKey)
      return
    }
    // When offline mode is active, always persist to localStorage so
    // the session survives browser restarts while the device is offline.
    const offlineMode = window.localStorage.getItem('offlineMode') === '1'
    if (persistent || offlineMode) {
      window.localStorage.setItem(storageKey, JSON.stringify(user))
      window.localStorage.setItem(persistKey, '1')
      window.sessionStorage.removeItem(storageKey)
    } else {
      window.sessionStorage.setItem(storageKey, JSON.stringify(user))
      window.localStorage.removeItem(storageKey)
      window.localStorage.removeItem(persistKey)
    }
  } catch {
    // ignore storage errors for this MVP
  }
}

const state = reactive({
  user: loadSession() as SessionUser | null
})

export const sessionStore = {
  state,
  isLoggedIn: computed(() => !!state.user),
  isAdmin: computed(() => state.user?.role === 'admin-master' || state.user?.role === 'admin-obra'),
  isPersistent: computed(() => {
    try { return window.localStorage.getItem(persistKey) === '1' } catch { return false }
  }),
  login(user: SessionUser, keepLoggedIn = false) {
    state.user = user
    saveSession(state.user, keepLoggedIn)
  },
  logout() {
    state.user = null
    saveSession(null)
    clearAuthToken()
  }
}
